/**
 * PPG Camera Handler
 * Manages WebRTC camera access, torch/flash control, and raw frame capture.
 * Extracts red/green channel intensity from each video frame.
 */

export interface CameraConfig {
  targetFPS: number;
  torchEnabled: boolean;
}

export interface FrameSample {
  timestamp: number;
  redMean: number;
  greenMean: number;
}

export interface CameraHandlerCallbacks {
  onFrame: (sample: FrameSample) => void;
  onError: (error: string) => void;
  onTorchStatus: (available: boolean) => void;
}

export class CameraHandler {
  private stream: MediaStream | null = null;
  private videoEl: HTMLVideoElement | null = null;
  private canvasEl: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private animFrameId: number | null = null;
  private lastFrameTime = 0;
  private frameInterval: number;
  private callbacks: CameraHandlerCallbacks;
  private torchAvailable = false;
  private running = false;

  constructor(config: CameraConfig, callbacks: CameraHandlerCallbacks) {
    this.frameInterval = 1000 / config.targetFPS;
    this.callbacks = callbacks;
  }

  /** Start camera stream with rear-facing camera and torch if available */
  async start(): Promise<void> {
    try {
      // Request rear camera with torch
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 320 },
          height: { ideal: 240 },
          frameRate: { ideal: 30, min: 15 },
        },
        audio: false,
      };

      this.stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Attempt to enable torch
      await this.enableTorch();

      // Set up hidden video element for frame capture
      this.videoEl = document.createElement('video');
      this.videoEl.srcObject = this.stream;
      this.videoEl.playsInline = true;
      this.videoEl.muted = true;
      this.videoEl.style.display = 'none';
      document.body.appendChild(this.videoEl);
      await this.videoEl.play();

      // Set up offscreen canvas for pixel extraction
      this.canvasEl = document.createElement('canvas');
      this.canvasEl.width = 64;
      this.canvasEl.height = 64;
      this.ctx = this.canvasEl.getContext('2d', { willReadFrequently: true });

      this.running = true;
      this.captureLoop(performance.now());
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Camera access denied';
      this.callbacks.onError(msg);
    }
  }

  /** Enable torch/flash on the video track if supported */
  private async enableTorch(): Promise<void> {
    if (!this.stream) return;
    const track = this.stream.getVideoTracks()[0];
    if (!track) return;

    try {
      const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
      if (capabilities.torch) {
        await track.applyConstraints({ advanced: [{ torch: true } as MediaTrackConstraintSet] });
        this.torchAvailable = true;
        this.callbacks.onTorchStatus(true);
      } else {
        this.torchAvailable = false;
        this.callbacks.onTorchStatus(false);
      }
    } catch {
      // Torch not supported — graceful fallback
      this.torchAvailable = false;
      this.callbacks.onTorchStatus(false);
    }
  }

  /** Main frame capture loop — throttled to target FPS */
  private captureLoop = (now: number): void => {
    if (!this.running) return;
    this.animFrameId = requestAnimationFrame(this.captureLoop);

    if (now - this.lastFrameTime < this.frameInterval) return;
    this.lastFrameTime = now;

    if (!this.videoEl || !this.ctx || !this.canvasEl) return;
    if (this.videoEl.readyState < 2) return;

    // Draw current video frame to offscreen canvas
    this.ctx.drawImage(this.videoEl, 0, 0, this.canvasEl.width, this.canvasEl.height);
    const imageData = this.ctx.getImageData(0, 0, this.canvasEl.width, this.canvasEl.height);
    const { redMean, greenMean } = this.extractChannelMeans(imageData.data);

    this.callbacks.onFrame({
      timestamp: now,
      redMean,
      greenMean,
    });
  };

  /**
   * Extract mean red and green channel values from RGBA pixel array.
   * Red channel is primary for PPG; green provides robustness.
   */
  private extractChannelMeans(pixels: Uint8ClampedArray): { redMean: number; greenMean: number } {
    let redSum = 0;
    let greenSum = 0;
    const pixelCount = pixels.length / 4;

    for (let i = 0; i < pixels.length; i += 4) {
      redSum += pixels[i];       // R
      greenSum += pixels[i + 1]; // G
    }

    return {
      redMean: redSum / pixelCount,
      greenMean: greenSum / pixelCount,
    };
  }

  /** Stop camera and release all resources */
  stop(): void {
    this.running = false;

    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }

    if (this.videoEl) {
      this.videoEl.remove();
      this.videoEl = null;
    }

    this.ctx = null;
    this.canvasEl = null;
  }

  get isTorchAvailable(): boolean {
    return this.torchAvailable;
  }
}
